/**
 * Tax Organizers Page
 * View and manage all tax organizers across clients.
 * Access via: Global Add → Tax Organizer
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { enhancedApi } from '../api';
import OrganizerQuestionnaire from '../components/OrganizerQuestionnaire';
import OrganizerBulkSendDialog from '../components/OrganizerBulkSendDialog';
import OrganizerTemplateEditor from '../components/OrganizerTemplateEditor';
import PageLayout from '../components/ui/PageLayout';
import PageHeaderActionsBar from '../components/ui/PageHeaderActionsBar';
import {
  Box, Typography, Button, Card, CardContent, Chip, Alert, CircularProgress,
  LinearProgress, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import DescriptionIcon from '@mui/icons-material/Description';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const UseTemplateForm = ({ template, clients, organizerTypes, onClose, onSubmit, saving }) => {
  const [client, setClient] = useState('');
  const [organizerType, setOrganizerType] = useState('');
  const [taxYear, setTaxYear] = useState(String(new Date().getFullYear()));
  const [name, setName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!client || !organizerType) return;
    onSubmit({
      client,
      organizer_type: organizerType,
      tax_year: parseInt(taxYear, 10) || new Date().getFullYear(),
      name: name || undefined,
      due_date: dueDate || undefined,
      notes: notes || undefined
    }, template);
  };

  return (
    <>
      <DialogTitle>Create organizer from template</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Client"
            value={client}
            onChange={(e) => setClient(e.target.value)}
            required
            sx={{ mb: 2 }}
          >
            <MenuItem value="">Select client</MenuItem>
            {clients.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name || c.company_name || `Client ${c.id}`}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Organizer type"
            value={organizerType}
            onChange={(e) => setOrganizerType(e.target.value)}
            required
            sx={{ mb: 2 }}
          >
            <MenuItem value="">Select type</MenuItem>
            {organizerTypes.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.name || t.display_name || `Type ${t.id}`}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Tax year"
            value={taxYear}
            onChange={(e) => setTaxYear(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField fullWidth label="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth label="Due date (optional)" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
          <TextField fullWidth label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving || !client || !organizerType}>
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </>
  );
};

const TaxOrganizersPage = () => {
  const navigate = useNavigate();
  const [organizers, setOrganizers] = useState([]);
  const [clients, setClients] = useState([]);
  const [organizerTypes, setOrganizerTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completionFilter, setCompletionFilter] = useState('all');
  const [sortBy, setSortBy] = useState('completion');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkSendModal, setShowBulkSendModal] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateCategory, setTemplateCategory] = useState('all');
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);
  const [useTemplateDialog, setUseTemplateDialog] = useState({ open: false, template: null });
  const [creatingOrganizer, setCreatingOrganizer] = useState(false);

  const completionStatusParam = completionFilter === 'all' ? undefined : (
    completionFilter === 'not_started' ? 'not_started' :
    completionFilter === 'in_progress' ? 'in_progress' : 'complete'
  );

  const fetchOrganizers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (completionStatusParam) params.completion_status = completionStatusParam;
      const response = await api.get('/api/organizers/', { params });
      if (response.data?.success) {
        setOrganizers(response.data.data || []);
      }
    } catch (err) {
      setError('Failed to fetch organizers');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await enhancedApi.getClients();
      const data = response.data;
      setClients(Array.isArray(data) ? data : []);
    } catch {
    }
  };

  const fetchOrganizerTypes = async () => {
    try {
      const response = await api.get('/api/organizer-types/');
      const data = response.data?.data ?? response.data?.results ?? (Array.isArray(response.data) ? response.data : []);
      setOrganizerTypes(Array.isArray(data) ? data : []);
    } catch {
      setOrganizerTypes([]);
    }
  };

  useEffect(() => {
    fetchOrganizers();
    fetchClients();
    fetchOrganizerTypes();
  }, []);

  useEffect(() => {
    fetchOrganizers();
  }, [completionFilter]);

  const fetchTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const res = await enhancedApi.getOrganizerTemplates();
      const data = res.data?.data ?? res.data;
      const raw = Array.isArray(data) ? data : [];
      const filtered = templateCategory === 'all'
        ? raw
        : raw.filter((t) => (t.category || '').toLowerCase() === templateCategory);
      setTemplates(filtered);
    } catch {
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    if (showTemplateGallery) fetchTemplates();
  }, [showTemplateGallery, templateCategory]);

  useEffect(() => {
    if (showCreateModal) fetchOrganizerTypes();
  }, [showCreateModal]);

  const handleCreateOrganizerFromQuestionnaire = async (organizerData) => {
    const { uploadedFiles = [], responses, ...rest } = organizerData;
    const clientId = rest.client || organizerData.client;
    if (!clientId || !rest.organizer_type) {
      setError('Please select Client and Organizer type');
      return;
    }
    setCreatingOrganizer(true);
    setError(null);
    try {
      const payload = {
        name: rest.name || `Organizer ${rest.tax_year || new Date().getFullYear()}`,
        tax_year: parseInt(rest.tax_year, 10) || new Date().getFullYear(),
        client_type: rest.client_type || 'existing_client',
        organizer_type: rest.organizer_type,
        client: clientId,
        due_date: rest.due_date || null,
        notes: rest.notes || '',
        assigned_to: Array.isArray(rest.assigned_to) ? rest.assigned_to : []
      };
      const response = await api.post('/api/organizers/', payload);
      if (response.data?.success) {
        const organizerId = response.data.data?.id;
        const organizerData = response.data.data;
        if (organizerId && responses && Object.keys(responses).length > 0 && organizerData?.sections) {
          for (const section of organizerData.sections) {
            for (const field of section.fields || []) {
              const val = responses[field.field_name] ?? (field.field_name === 'comments' ? responses.additional_comments : undefined);
              if (val === undefined || val === null || val === '') continue;
              const payload = { field: field.id };
              if (field.field_type === 'number') payload.number_value = parseFloat(val) || 0;
              else if (field.field_type === 'boolean') payload.boolean_value = !!val;
              else if (field.field_type === 'date') payload.date_value = val;
              else payload.text_value = String(val);
              try {
                await api.post(`/api/organizers/${organizerId}/responses/`, payload);
              } catch {
              }
            }
          }
        }
        if (organizerId && uploadedFiles?.length > 0) {
          for (const file of uploadedFiles) {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('filename', file.name);
            fd.append('file_size', file.size);
            fd.append('file_type', file.type || '');
            fd.append('is_client_upload', 'false');
            try {
              await api.post(`/api/organizers/${organizerId}/documents/`, fd);
            } catch {
            }
          }
        }
        setShowCreateModal(false);
        fetchOrganizers();
        if (organizerId) navigate(`/organizer/${organizerId}`);
      } else {
        setError(response.data?.error || 'Failed to create organizer');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCreatingOrganizer(false);
    }
  };

  const handleSendToClientFromQuestionnaire = () => {
    setError('Create the organizer first, then use "Send to Client" from the organizer list.');
  };

  const handleSendToClient = async (organizerId) => {
    try {
      await api.post(`/api/organizers/${organizerId}/send-to-client/`);
      fetchOrganizers();
    } catch (err) {
      setError('Failed to send organizer to client');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      sent_to_client: 'info',
      in_progress: 'warning',
      completed: 'success',
      needs_review: 'warning',
      archived: 'default'
    };
    return colors[status] || 'default';
  };

  const getProgressColor = (pct) => {
    if (pct <= 25) return 'error.main';
    if (pct <= 50) return 'warning.main';
    if (pct <= 75) return 'warning.light';
    return 'success.main';
  };

  const getCompletionStatusLabel = (pct) => {
    if (pct == null || pct === 0) return 'Not Started';
    if (pct >= 100) return 'Complete';
    return 'In Progress';
  };

  const getCompletionStatusChipColor = (pct) => {
    if (pct == null || pct === 0) return 'default';
    if (pct >= 100) return 'success';
    return 'warning';
  };

  const filteredOrganizers = organizers.filter(() => true);

  const sortedOrganizers = [...filteredOrganizers].sort((a, b) => {
    if (sortBy === 'completion') {
      const pa = a.completion_pct ?? a.progress_percentage ?? 0;
      const pb = b.completion_pct ?? b.progress_percentage ?? 0;
      return pb - pa;
    }
    if (sortBy === 'client') {
      const na = (a.client_name || '').toLowerCase();
      const nb = (b.client_name || '').toLowerCase();
      return na.localeCompare(nb);
    }
    if (sortBy === 'due_date') {
      const da = a.due_date ? new Date(a.due_date).getTime() : 0;
      const db = b.due_date ? new Date(b.due_date).getTime() : 0;
      return da - db;
    }
    return 0;
  });

  const handleOptionSelect = (option) => {
    if (option === 'home') navigate('/dashboard');
    else if (option === 'workflows') navigate('/workflows');
    else if (option === 'tax-organizer') setShowTemplateGallery(true);
  };

  const handleUseTemplate = (template) => {
    setUseTemplateDialog({ open: true, template });
  };

  const handleCreateFromTemplate = async (data, template) => {
    const tpl = template ?? useTemplateDialog.template;
    if (!tpl?.id || !data?.client || !data?.organizer_type) return;
    setCreatingOrganizer(true);
    setError(null);
    try {
      const res = await enhancedApi.createOrganizerFromTemplate(tpl.id, {
        client: data.client,
        organizer_type: data.organizer_type,
        tax_year: data.tax_year || new Date().getFullYear(),
        name: data.name || undefined,
        due_date: data.due_date || undefined,
        notes: data.notes || undefined,
        assigned_to: data.assigned_to || undefined
      });
      const newOrg = res.data?.data ?? res.data;
      const id = newOrg?.id ?? newOrg?.organizer?.id ?? newOrg?.id;
      setUseTemplateDialog({ open: false, template: null });
      setShowTemplateGallery(false);
      fetchOrganizers();
      if (id) navigate(`/organizer/${id}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCreatingOrganizer(false);
    }
  };

  return (
    <PageLayout onOptionSelect={handleOptionSelect}>
      <PageHeaderActionsBar
        title="Tax Organizers"
        description="Gather and organize tax information from clients. Send organizers to clients or fill them out yourself."
        primaryAction={{ label: 'New Organizer', onClick: () => setShowTemplateGallery(true), icon: <AddIcon /> }}
        secondaryActions={[
          { label: 'Customize Templates', onClick: () => setShowTemplateEditor(true), variant: 'outlined' },
          { label: 'Bulk Send', onClick: () => setShowBulkSendModal(true), variant: 'outlined', icon: <ForwardToInboxIcon /> },
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[
            { id: 'all', label: 'All' },
            { id: 'not_started', label: 'Not Started' },
            { id: 'in_progress', label: 'In Progress' },
            { id: 'complete', label: 'Complete' }
          ].map(({ id, label }) => (
            <Button
              key={id}
              variant={completionFilter === id ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setCompletionFilter(id)}
            >
              {label}
            </Button>
          ))}
        </Box>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={sortBy}
            label="Sort by"
            onChange={(e) => setSortBy(e.target.value)}
          >
            <MenuItem value="completion">Completion %</MenuItem>
            <MenuItem value="client">Client Name</MenuItem>
            <MenuItem value="due_date">Due Date</MenuItem>
          </Select>
        </FormControl>
      </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {[1, 2, 3, 4].map((i) => (
                <Box key={i} sx={{ flex: 1, minWidth: 80 }}>
                  <Box component="span" sx={{ display: 'block', height: 36, borderRadius: 1, bgcolor: 'grey.200' }} />
                </Box>
              ))}
            </Box>
            {[1, 2, 3, 4, 5].map((i) => (
              <Box key={i} sx={{ height: 120, borderRadius: 2, bgcolor: 'grey.200' }} />
            ))}
          </Box>
        ) : sortedOrganizers.length === 0 ? (
          <Box sx={{ py: 10, px: 3, textAlign: 'center' }}>
            <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <DescriptionIcon sx={{ fontSize: 40, color: 'grey.500' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>No organizers yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
              Create a tax organizer to gather information from your clients.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowTemplateGallery(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>
              New Organizer
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sortedOrganizers.map((org) => {
              const pct = org.completion_pct ?? org.progress_percentage ?? 0;
              return (
              <Card key={org.id} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 0 }}>
                <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6">{org.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {org.organizer_type_name} • Tax Year {org.tax_year}
                        {org.client_name && ` • ${org.client_name}`}
                      </Typography>
                      <Box display="flex" gap={1} mt={1} alignItems="center" flexWrap="wrap">
                        <Chip
                          label={getCompletionStatusLabel(pct)}
                          color={getCompletionStatusChipColor(pct)}
                          size="small"
                        />
                        <Chip label={org.status} color={getStatusColor(org.status)} size="small" variant="outlined" />
                        {org.due_date && (
                          <Typography variant="caption" color="text.secondary">
                            Due: {new Date(org.due_date).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, maxWidth: 280 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, Math.max(0, pct))}
                          sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: 1,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: getProgressColor(pct)
                            }
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {pct}%
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => window.open(`/organizer/${org.id}`, '_blank')}
                      >
                        View
                      </Button>
                      {org.status === 'draft' && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<SendIcon />}
                          onClick={() => handleSendToClient(org.id)}
                        >
                          Send to Client
                        </Button>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
            })}
          </Box>
        )}

        {/* Template Editor */}
        {showTemplateEditor && (
          <OrganizerTemplateEditor
            open={showTemplateEditor}
            onClose={() => setShowTemplateEditor(false)}
            organizerTypes={organizerTypes}
          />
        )}
        {/* Bulk Send Dialog */}
        {showBulkSendModal && (
          <OrganizerBulkSendDialog
            open={showBulkSendModal}
            onClose={() => setShowBulkSendModal(false)}
            clients={clients}
            organizerTypes={organizerTypes}
            onSuccess={fetchOrganizers}
          />
        )}
        {/* Create Organizer - Full questionnaire (same as client page) */}
        {showCreateModal && (
          <OrganizerQuestionnaire
            clients={clients}
            organizerTypes={organizerTypes}
            onClose={() => !creatingOrganizer && setShowCreateModal(false)}
            onSubmit={handleCreateOrganizerFromQuestionnaire}
            onSendToClient={handleSendToClientFromQuestionnaire}
            saving={creatingOrganizer}
            error={error}
            onClearError={() => setError(null)}
          />
        )}

        {/* Template Gallery Dialog */}
        <Dialog open={showTemplateGallery} onClose={() => { setShowTemplateGallery(false); setExpandedTemplateId(null); }} maxWidth="md" fullWidth>
          <DialogTitle>Choose a template</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {['all', 'individual', 'business', 'trust', 'estate', 'bookkeeping'].map((cat) => (
                <Chip
                  key={cat}
                  label={cat.charAt(0).toUpperCase() + cat.slice(1)}
                  onClick={() => setTemplateCategory(cat)}
                  color={templateCategory === cat ? 'primary' : 'default'}
                  variant={templateCategory === cat ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
            </Box>
            {templatesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : templates.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No templates found. Try a different category or add templates in Customize Templates.</Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {templates.map((tpl) => {
                  const expanded = expandedTemplateId === tpl.id;
                  const sections = tpl.form_schema?.sections ?? [];
                  return (
                    <Grid item xs={12} sm={6} key={tpl.id}>
                      <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flex: 1 }}>
                          <Box
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
                            onClick={() => setExpandedTemplateId(expanded ? null : tpl.id)}
                          >
                            <Typography variant="subtitle1" fontWeight={600}>
                              {tpl.name || 'Unnamed Template'}
                            </Typography>
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {tpl.description || 'No description'}
                          </Typography>
                          <Chip label={tpl.category || 'Uncategorized'} size="small" sx={{ mb: 1 }} />
                          {expanded && sections.length > 0 && (
                            <Box sx={{ mt: 1.5, pl: 1, borderLeft: 2, borderColor: 'divider' }}>
                              <Typography variant="caption" color="text.secondary" component="div">
                                Sections:
                              </Typography>
                              {sections.map((sec, i) => (
                                <Typography key={i} variant="caption" component="div" sx={{ pl: 1 }}>
                                  • {sec.name || sec.title || `Section ${i + 1}`}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </CardContent>
                        <Box sx={{ p: 1.5, pt: 0 }}>
                          <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            onClick={() => handleUseTemplate(tpl)}
                            disabled={creatingOrganizer}
                          >
                            Use Template
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setShowTemplateGallery(false); setShowCreateModal(true); }}>
              Create from scratch
            </Button>
            <Button onClick={() => setShowTemplateGallery(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* Use Template - Client & Organizer Type */}
        <Dialog
          open={useTemplateDialog.open}
          onClose={() => !creatingOrganizer && setUseTemplateDialog({ open: false, template: null })}
          maxWidth="sm"
          fullWidth
        >
          <UseTemplateForm
            key={useTemplateDialog.template?.id ?? 'empty'}
            template={useTemplateDialog.template}
            clients={clients}
            organizerTypes={organizerTypes}
            onClose={() => !creatingOrganizer && setUseTemplateDialog({ open: false, template: null })}
            onSubmit={handleCreateFromTemplate}
            saving={creatingOrganizer}
          />
        </Dialog>
    </PageLayout>
  );
};

export default TaxOrganizersPage;
